/**
 * API Service - handles all backend communication
 */

// Backend runs on port 3000, frontend on 3001. Use REACT_APP_API_BASE in production.
const API_BASE =
    process.env.REACT_APP_API_BASE && process.env.REACT_APP_API_BASE.trim().length > 0
        ? process.env.REACT_APP_API_BASE
        : 'http://localhost:3000/api';

import { Phase, TreeNode, Session, ReportItem, ShoppingListItem } from '../types';

// Fetch tree for a phase (pass area to get examples embedded in each node)
export async function fetchTree(phase: Phase, area?: string): Promise<TreeNode[]> {
    const url = area
        ? `${API_BASE}/trees/${phase}?area=${encodeURIComponent(area)}`
        : `${API_BASE}/trees/${phase}`;
    const response = await fetch(url);
    if (!response.ok) {
        throw new Error(`Failed to fetch tree for ${phase}`);
    }
    const data = await response.json();
    return data.tree;
}

// Create a new session
export async function createSession(phase: Phase, area: string): Promise<Session> {
    const response = await fetch(`${API_BASE}/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phase, area })
    });
    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to create session');
    }
    return response.json();
}

// Record an answer
export async function recordAnswer(
    sessionId: string, 
    questionId: string, 
    value: 'yes' | 'no' | 'add-to-cart'
): Promise<Session> {
    const response = await fetch(`${API_BASE}/session/${sessionId}/answer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ questionId, value })
    });
    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to record answer');
    }
    return response.json();
}

// Undo the last recorded answer
export async function undoLastAnswer(sessionId: string): Promise<void> {
    const response = await fetch(`${API_BASE}/session/${sessionId}/answer/last`, {
        method: 'DELETE',
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error((error as any).message || 'Failed to undo answer');
    }
}

// Get the full report
export async function fetchReport(sessionId: string): Promise<{
    sessionId: string;
    phase: Phase;
    area: string;
    report: ReportItem[];
}> {
    const response = await fetch(`${API_BASE}/session/${sessionId}/report`);
    if (!response.ok) {
        throw new Error('Failed to fetch report');
    }
    return response.json();
}

// Get the shopping bag
export async function fetchCart(sessionId: string): Promise<{
    sessionId: string;
    phase: Phase;
    area: string;
    cartCount: number;
    shoppingList: ShoppingListItem[];
}> {
    const response = await fetch(`${API_BASE}/session/${sessionId}/cart`);
    if (!response.ok) {
        throw new Error('Failed to fetch cart');
    }
    return response.json();
}

// --- Admin / Indicator control panel ---

export interface AdminIndicatorRow {
    id: string;
    question: string;
    recommendation: string;
    detailedQuestion: string;
    enabling: boolean;
}

export interface AdminExampleRow {
    indicatorId: string;
    exampleArea: string;
    example: string;
}

export interface AdminPhaseData {
    indicators: AdminIndicatorRow[];
    examples: AdminExampleRow[];
}

export interface TrashIndicatorRow extends AdminIndicatorRow {
    original_phase: string;
}

export interface TrashPhaseData {
    indicators: TrashIndicatorRow[];
    examples: AdminExampleRow[];
}

export interface AdminIndicatorsResponse {
    phases: Record<Phase, AdminPhaseData> & { trash?: TrashPhaseData };
    /** Current version number per phase, used for optimistic locking. */
    versions: Record<Phase, number>;
}

export async function fetchAdminIndicators(): Promise<AdminIndicatorsResponse> {
    const response = await fetch(`${API_BASE}/admin/indicators`);
    if (!response.ok) {
        let message = `Failed to fetch indicators (${response.status})`;
        try {
            const body = await response.json();
            if (body?.message) message = body.message;
        } catch {
            if (response.status === 0 || response.type === 'error') message = 'Cannot reach server. Is the backend running on the API port?';
        }
        throw new Error(message);
    }
    return response.json();
}

export class ConflictError extends Error {
    readonly currentVersion: number;
    constructor(message: string, currentVersion: number) {
        super(message);
        this.name = 'ConflictError';
        this.currentVersion = currentVersion;
    }
}

export async function saveAdminIndicators(
    phase: Phase,
    data: {
        indicators: AdminIndicatorRow[];
        examples: Array<{ indicator_id: string; example_area: string; example: string }>;
        /** Version loaded from the server; omit to skip conflict checking. */
        version?: number;
    }
): Promise<{ ok: boolean; phase: Phase; indicatorsCount: number; examplesCount: number; version: number }> {
    const response = await fetch(`${API_BASE}/admin/indicators/${phase}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
    });
    const text = await response.text();
    if (!response.ok) {
        let err: any = {};
        try { err = JSON.parse(text); } catch { /* non-JSON error body (e.g. HTML from proxy) */ }
        if (response.status === 409) {
            throw new ConflictError(err.message || 'Conflict', err.currentVersion ?? 0);
        }
        throw new Error(err.message || `Save failed (HTTP ${response.status})`);
    }
    try {
        return JSON.parse(text);
    } catch {
        throw new Error('Unexpected response from server');
    }
}

export async function trashIndicator(phase: Phase, indicatorId: string): Promise<{ ok: boolean; phase: Phase; indicatorId: string; trashed: boolean }> {
    const response = await fetch(`${API_BASE}/admin/indicators/${phase}/trash`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ indicatorId }),
    });
    const text = await response.text();
    if (!response.ok) {
        let message = `Failed to trash (${response.status})`;
        try {
            if (text) {
                const err = JSON.parse(text);
                if (err?.message) message = err.message;
            }
        } catch {
            if (text && text.trim().length < 200) message = text;
        }
        throw new Error(message);
    }
    if (!text) throw new Error('Empty response from server');
    try {
        return JSON.parse(text);
    } catch {
        throw new Error('Invalid response from server');
    }
}

export async function restoreFromTrash(indicatorId: string, restoreAsId?: string, originalPhase?: string): Promise<{ ok: boolean; phase: Phase; indicatorId: string; restored: boolean }> {
    const payload: Record<string, string> = { indicatorId };
    if (restoreAsId) payload.restoreAsId = restoreAsId;
    if (originalPhase) payload.originalPhase = originalPhase;
    const response = await fetch(`${API_BASE}/admin/indicators/trash/restore`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
    });
    const text = await response.text();
    if (!response.ok) {
        let message = `Failed to restore (${response.status})`;
        try {
            if (text) {
                const err = JSON.parse(text);
                if (err?.message) message = err.message;
            }
        } catch {
            if (text && text.trim().length < 200) message = text;
        }
        throw new Error(message);
    }
    if (!text) throw new Error('Empty response from server');
    try {
        return JSON.parse(text);
    } catch {
        throw new Error('Invalid response from server');
    }
}

// ─── Expert Feedback ────────────────────────────────────────────────

export async function submitFeedback(data: Record<string, any>): Promise<{ ok: boolean; id: number }> {
    const response = await fetch(`${API_BASE}/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Failed to submit feedback');
    return response.json();
}

export async function fetchFeedbackList(): Promise<{ feedback: any[] }> {
    const response = await fetch(`${API_BASE}/feedback`);
    if (!response.ok) throw new Error('Failed to fetch feedback');
    return response.json();
}

export async function fetchFeedbackStats(): Promise<{ stats: Record<string, number> }> {
    const response = await fetch(`${API_BASE}/feedback/stats`);
    if (!response.ok) throw new Error('Failed to fetch feedback stats');
    return response.json();
}

export async function markFeedbackRead(id: number): Promise<{ ok: boolean }> {
    const response = await fetch(`${API_BASE}/feedback/${id}/read`, { method: 'PATCH' });
    if (!response.ok) throw new Error('Failed to mark feedback as read');
    return response.json();
}

export async function createIndicator(
    phase: Phase,
    data: { id: string; question: string; recommendation: string; detailedQuestion: string; enabling?: boolean; examples?: Array<{ indicator_id: string; example_area: string; example: string }> }
): Promise<{ ok: boolean; phase: Phase; indicatorId: string; created: boolean }> {
    const response = await fetch(`${API_BASE}/admin/indicators/${phase}/new`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
    });
    if (!response.ok) {
        let message = `Failed to create (${response.status})`;
        try {
            const text = await response.text();
            const err = text ? JSON.parse(text) : {};
            if (err?.message) message = err.message;
        } catch {
            // response was not JSON (e.g. HTML error page)
        }
        throw new Error(message);
    }
    const text = await response.text();
    if (!text) throw new Error('Empty response');
    try {
        return JSON.parse(text);
    } catch {
        throw new Error('Invalid response from server');
    }
}
